import { memo, useCallback, useEffect, useRef, useState, type JSX } from 'react'

import { Box } from '@mui/material'
import { AvcTask } from '@nativewaves/nw-cloud-sdk/content'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Table as ReactTableType, RowSelectionState } from '@tanstack/react-table'
import { VirtualItem } from '@tanstack/react-virtual'
import { pick } from 'lodash-es'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { Dialog } from '@shared/components/MaterialUIEnhancements/Dialog'
import { SearchBar } from '@shared/components/SearchBar'
import { SelectionToolbar, Table } from '@shared/components/Table'
import { ActionStrip, BasicListOperation } from '@shared/layouts'
import {
  CreationDialogContent,
  RowActions,
  SelectionToolbarActions,
  SuccessPanelActions,
  useAsyncContentCaching,
  useColumns,
} from 'domains/Workspaces/ContentFlows/AvcTasks'
import { BodyCellLink } from 'layouts/BodyCellLink'
import { avcTaskConfigRoute } from 'pages/NWPlatform/Workspaces/Workspace/AvcTasks/AvcTask/Routing'
import { avcTasksOverviewRoute } from 'pages/NWPlatform/Workspaces/Workspace/AvcTasks/Routing'
import { avcTaskQueryKeys } from 'services/queryKeys'
import { getAvcTaskStateColor } from 'utils'

const OverviewRaw = (): JSX.Element => {
  const { t } = useTranslation(['common', 'domain', 'entity'])
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  const navigate = useNavigate({ from: avcTasksOverviewRoute.fullPath })
  const routeParams = avcTasksOverviewRoute.useParams()
  const { query } = avcTasksOverviewRoute.useSearch()

  const tableInstance = useRef<ReactTableType<AvcTask>>(undefined)

  const [virtualItems, setVirtualItems] = useState<VirtualItem[] | undefined>(undefined)
  const [creationDialogOpen, setCreationDialogOpen] = useState(false)
  const [loadingRows, setLoadingRows] = useState<string[]>([])

  const { workspaceQuery, avcTasksQuery, setBatchGetEnabled } = useAsyncContentCaching({
    virtualItems,
    setLoadingRows,
  })

  const columns = useColumns()

  const handleSuccess = useCallback(
    (data: AvcTask) => {
      enqueueSnackbar(`${t('domain:Workspace.AvcTask.taskCreated', { name: data.name })}`, {
        description: `${t('domain:Workspace.AvcTask.taskCreatedDescription')}`,
        variant: 'panel',
        persist: true,
        icon: 'success',
        Actions: (props) => <SuccessPanelActions {...props} {...routeParams} avcTaskId={data.id} />,
      })
    },
    [enqueueSnackbar, routeParams, t],
  )

  useEffect(() => {
    if (avcTasksQuery.isSuccess) {
      tableInstance.current?.setRowSelection((old) =>
        Object.keys(old).reduce((acc, newItem) => {
          const newItemFound = avcTasksQuery.data.flat.find((task) => task.id === newItem)
          if (newItemFound) {
            return { ...acc, [newItem]: true }
          }
          return acc
        }, {} as RowSelectionState),
      )
    }
  }, [avcTasksQuery.data?.flat, avcTasksQuery.isSuccess])

  return (
    <>
      <ActionStrip>
        <SearchBar
          placeholder={t('domain:Workspace.AvcTask.searchFlows')}
          onSearch={({ search }) => navigate({ search: { query: search } })}
          defaultValue={query}
        />

        <BasicListOperation
          refreshAction={useCallback(() => {
            queryClient.cancelQueries({ queryKey: avcTaskQueryKeys.batchGets() })
            setBatchGetEnabled(false)
            queryClient.invalidateQueries({
              queryKey: avcTaskQueryKeys.lists(),
            })
          }, [queryClient, setBatchGetEnabled])}
          createAction={useCallback(() => setCreationDialogOpen(true), [])}
        />
      </ActionStrip>

      <Table
        getTableInstance={(instance) => (tableInstance.current = instance)}
        size={48}
        headSize={44}
        data={avcTasksQuery.data?.flat ?? []}
        columns={columns}
        isInitialLoadingGlobal={workspaceQuery.isLoading || avcTasksQuery.isLoading}
        isInitialLoadingByRowByID={loadingRows.reduce((acc, row) => ({ ...acc, [row]: [] }), {})}
        enableLinking
        LinkProps={(row, content, cell) => (
          <BodyCellLink
            sx={[!!cell.column.columnDef.meta?.align && { justifyContent: cell.column.columnDef.meta.align }]}
            from={avcTasksOverviewRoute.fullPath}
            to={avcTaskConfigRoute.to}
            params={{ avcTaskId: row.original.id }}
          >
            {content}
          </BodyCellLink>
        )}
        excludeFieldsFromLinking={['id', 'alerts', '_start_stop']}
        enableInfinity
        infinityProps={pick(avcTasksQuery, ['hasNextPage', 'isFetchingNextPage', 'fetchNextPage'])}
        enableVirtualization
        virtualizedProps={{
          getVirtualItems: (instance) => setVirtualItems(instance),
        }}
        ActionMenuProps={(row) => ({
          title: row.original.name,
          entity: t('entity:content.avcTask', { count: 1 }),
        })}
        RowActions={(props) => <RowActions {...props} />}
        renderSelectionToolbar={({ selectedRows }) => (
          <SelectionToolbar
            selectedRows={selectedRows}
            title={t('entity:content.avcTask', { count: 2 })}
            discardSelection={() => tableInstance.current?.resetRowSelection()}
            subTitleMapper={(row) => (
              <Box
                key={row.original.id}
                component="span"
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: (theme) => getAvcTaskStateColor(row.original.state, theme.palette.mode),
                }}
              />
            )}
            Actions={
              tableInstance.current && (
                <SelectionToolbarActions selectedRows={selectedRows} tableInstance={tableInstance.current} />
              )
            }
          />
        )}
        tableOptions={{
          getRowId: (avcTask) => avcTask.id,
        }}
      />

      <Dialog open={creationDialogOpen} onClose={() => setCreationDialogOpen(false)} disableRestoreFocus>
        <CreationDialogContent onSuccess={handleSuccess} />
      </Dialog>
    </>
  )
}

export const Overview = memo(OverviewRaw)
